import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import { Add as AddIcon, Edit as EditIcon } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { PageFilter } from '../../../state/app';
import { AppState } from '../../../state/configureStore';
import {
  clearWorkingGroup,
  fetchWorkingGroupList,
  WorkingGroup,
  WorkingGroupListFilter,
  WorkingGroupState,
} from '../../../state/workingGroup';
import {
  PageContainer,
  PageTitle,
  SearchDropdownField,
  SearchField,
  TableLoading,
} from '../../components';
import { useFilter, useHasRole } from '../../hooks';
import roleRoute from '../roleRoute.config';
import useStyles from './workingGroupList.style';

const WorkingGroupList: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const isDisabled = !useHasRole(roleRoute.createWorkingGroup);

  const {
    items: workingGroups,
    totalItems,
    loading,
  } = useSelector<AppState, WorkingGroupState>((state) => state.workingGroup);

  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 15,
  });
  const [filters, setFilters] = useFilter<WorkingGroupListFilter>({
    'Active.EqualsTo': true,
  });

  useEffect(() => {
    dispatch(fetchWorkingGroupList(filters, paging));
    return function cleanUp() {
      dispatch(clearWorkingGroup());
    };
  }, [dispatch, paging, filters]);

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { target } = event;
    setFilters((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const addNewWorkingGroup = (): void => {
    history.push('/settings/workingGroups/form');
  };

  const editWorkingGroup = (id: WorkingGroup['id']): void => {
    history.push(`/settings/workingGroups/form/${id}`);
  };

  return (
    <PageContainer>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h5">Working Group</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={addNewWorkingGroup}
              size="large"
              disabled={isDisabled}
            >
              <AddIcon className={classes.iconButton} />
              New Working Group
            </Button>
          </Box>
        </Grid>
        <Grid item xs={2}>
          <SearchField
            fullWidth
            filterKey="Name.startsWith"
            filters={filters}
            label="Name"
            onChange={handleChangeFilter}
          />
        </Grid>
        <Grid item xs={2}>
          <SearchField
            fullWidth
            filterKey="Owner.FullNameWithUserName.Contains"
            filters={filters}
            label="Owner"
            onChange={handleChangeFilter}
          />
        </Grid>
        <Grid item xs={2}>
          <SearchField
            fullWidth
            filterKey="Role.Name.StartsWith"
            filters={filters}
            label="Role"
            onChange={handleChangeFilter}
          />
        </Grid>
        <Grid item xs={2}>
          <SearchDropdownField
            label="Status"
            filterKey={'Active.EqualsTo'}
            filters={filters}
            onChange={handleChangeFilter}
            id="status"
          >
            <MenuItem value={''}>All</MenuItem>
            <MenuItem value={'true'}>Active</MenuItem>
            <MenuItem value={'false'}>Inactive</MenuItem>
          </SearchDropdownField>
        </Grid>
      </Grid>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>No.</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableLoading isLoading={loading} colSpan={7}>
            {workingGroups.map((workingGroup, index) => {
              return (
                <TableRow key={workingGroup.id}>
                  <TableCell>{index + paging.pageSize * paging.pageNumber + 1}</TableCell>
                  <TableCell>{workingGroup.name}</TableCell>
                  <TableCell>{workingGroup.description}</TableCell>
                  <TableCell>{workingGroup.owner?.fullNameWithUserName}</TableCell>
                  <TableCell>{workingGroup.role?.name}</TableCell>
                  <TableCell>
                    {
                      <Chip
                        size="small"
                        variant="outlined"
                        label={workingGroup.active ? 'Active' : 'Inactive'}
                        color={workingGroup.active ? 'primary' : 'secondary'}
                      />
                    }
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="edit"
                      size="small"
                      onClick={() => editWorkingGroup(workingGroup.id)}
                      disabled={isDisabled}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableLoading>
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[15, 25, 35]}
        component="div"
        count={totalItems}
        rowsPerPage={paging.pageSize}
        page={paging.pageNumber}
        onPageChange={handleChangePageNumber}
        onRowsPerPageChange={handleChangePageSize}
      />
    </PageContainer>
  );
};

export default WorkingGroupList;
